import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    Users, Search, Filter, Plus, X, ExternalLink, Edit2, Trash2,
    Instagram, MapPin, TrendingUp, ChevronUp, ChevronDown, Copy
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AddressInput from '../components/AddressInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const InfluencersPage = () => {
    const { api, user } = useAuth();
    const [influencers, setInfluencers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [sortBy, setSortBy] = useState('followers');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showFilters, setShowFilters] = useState(false);
    
    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingInfluencer, setEditingInfluencer] = useState(null);
    
    // New influencer form
    const [newInfluencer, setNewInfluencer] = useState({
        name: '',
        instagram_url: '',
        followers: '',
        followers_display: '',
        categories: [],
        location: '',
        collaboration_details: '',
        notes: ''
    });
    
    const [newCategory, setNewCategory] = useState('');
    
    const isAdmin = user?.role === 'chef' || user?.role === 'admin';
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (categoryFilter) params.append('category', categoryFilter);
            if (locationFilter) params.append('location', locationFilter);
            params.append('sort_by', sortBy);
            params.append('sort_order', sortOrder);
            
            const [influencersRes, categoriesRes, locationsRes] = await Promise.all([
                api.get(`/influencers?${params.toString()}`),
                api.get('/influencers/categories'),
                api.get('/influencers/locations')
            ]);
            
            setInfluencers(influencersRes.data || []);
            setCategories(categoriesRes.data || []);
            setLocations(locationsRes.data || []);
        } catch (err) {
            console.error('Failed to fetch influencers:', err);
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [api, searchTerm, categoryFilter, locationFilter, sortBy, sortOrder]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Create influencer
    const handleCreate = async () => {
        if (!newInfluencer.name.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }
        
        try {
            const data = {
                ...newInfluencer,
                followers: newInfluencer.followers ? parseInt(newInfluencer.followers) : null,
                instagram_url: newInfluencer.instagram_url ? 
                    (newInfluencer.instagram_url.startsWith('http') ? newInfluencer.instagram_url : `https://www.instagram.com/${newInfluencer.instagram_url}`)
                    : null
            };
            
            await api.post('/influencers', data);
            toast.success('Influencer erstellt!');
            setShowAddModal(false);
            setNewInfluencer({
                name: '', instagram_url: '', followers: '', followers_display: '',
                categories: [], location: '', collaboration_details: '', notes: ''
            });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
        }
    };
    
    // Update influencer
    const handleUpdate = async () => {
        if (!editingInfluencer?.name?.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }
        
        try {
            const data = {
                ...editingInfluencer,
                followers: editingInfluencer.followers ? parseInt(editingInfluencer.followers) : null
            };
            
            await api.put(`/influencers/${editingInfluencer.id}`, data);
            toast.success('Gespeichert!');
            setShowEditModal(false);
            setEditingInfluencer(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        }
    };
    
    // Delete influencer
    const handleDelete = async (id) => {
        if (!window.confirm('Influencer wirklich löschen?')) return;
        
        try {
            await api.delete(`/influencers/${id}`);
            toast.success('Gelöscht!');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };
    
    // Add category to new influencer
    const addCategory = () => {
        if (newCategory.trim() && !newInfluencer.categories.includes(newCategory.trim())) {
            setNewInfluencer({
                ...newInfluencer,
                categories: [...newInfluencer.categories, newCategory.trim()]
            });
            setNewCategory('');
        }
    };
    
    // Remove category
    const removeCategory = (cat) => {
        setNewInfluencer({
            ...newInfluencer,
            categories: newInfluencer.categories.filter(c => c !== cat)
        });
    };
    
    // Copy Instagram URL
    const copyInstagram = (url) => {
        navigator.clipboard.writeText(url);
        toast.success('Instagram-Link kopiert!');
    };
    
    // Format followers
    const formatFollowers = (count) => {
        if (!count) return '-';
        if (count >= 1000000) return `${(count / 1000000).toFixed(1)} Mio`;
        if (count >= 1000) return `${(count / 1000).toFixed(0)}k`;
        return count.toString();
    };
    
    // Toggle sort
    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };
    
    const activeFilterCount = [categoryFilter, locationFilter].filter(f => f).length;

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
                            <Instagram className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Influencer Verwaltung</h1>
                            <p className="text-sm text-muted-foreground">
                                {influencers.length} Influencer
                            </p>
                        </div>
                    </div>
                    
                    {isAdmin && (
                        <Button onClick={() => setShowAddModal(true)} className="gap-2">
                            <Plus className="w-4 h-4" />
                            Influencer hinzufügen
                        </Button>
                    )}
                </div>
                
                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Suchen nach Name, Kategorie, Ort..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant={showFilters ? "default" : "outline"}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Filter className="w-4 h-4 mr-2" />
                            Filter
                            {activeFilterCount > 0 && (
                                <Badge variant="destructive" className="ml-2 px-1.5 py-0 text-xs">
                                    {activeFilterCount}
                                </Badge>
                            )}
                        </Button>
                        {activeFilterCount > 0 && (
                            <Button variant="ghost" onClick={() => { setCategoryFilter(''); setLocationFilter(''); }}>
                                <X className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </div>
                
                {/* Filter Panel */}
                {showFilters && (
                    <Card>
                        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Kategorie" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">Alle Kategorien</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            <Select value={locationFilter} onValueChange={setLocationFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Standort" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">Alle Standorte</SelectItem>
                                    {locations.map(loc => (
                                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            <Select value={`${sortBy}-${sortOrder}`} onValueChange={(v) => {
                                const [field, order] = v.split('-');
                                setSortBy(field);
                                setSortOrder(order);
                            }}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sortierung" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="followers-desc">Follower (absteigend)</SelectItem>
                                    <SelectItem value="followers-asc">Follower (aufsteigend)</SelectItem>
                                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                                    <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>
                )}
                
                {/* Table */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                ) : influencers.length === 0 ? (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">Keine Influencer gefunden</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b">
                                    <th 
                                        className="text-left p-3 cursor-pointer hover:bg-muted/50"
                                        onClick={() => toggleSort('name')}
                                    >
                                        <div className="flex items-center gap-1">
                                            Name
                                            {sortBy === 'name' && (
                                                sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                            )}
                                        </div>
                                    </th>
                                    <th 
                                        className="text-left p-3 cursor-pointer hover:bg-muted/50"
                                        onClick={() => toggleSort('followers')}
                                    >
                                        <div className="flex items-center gap-1">
                                            <TrendingUp className="w-4 h-4" />
                                            Follower
                                            {sortBy === 'followers' && (
                                                sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                                            )}
                                        </div>
                                    </th>
                                    <th className="text-left p-3">Kategorien</th>
                                    <th 
                                        className="text-left p-3 cursor-pointer hover:bg-muted/50"
                                        onClick={() => toggleSort('location')}
                                    >
                                        <div className="flex items-center gap-1">
                                            <MapPin className="w-4 h-4" />
                                            Ort
                                        </div>
                                    </th>
                                    <th className="text-left p-3">Preise/Details</th>
                                    <th className="text-right p-3">Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {influencers.map(inf => (
                                    <tr key={inf.id} className="border-b hover:bg-muted/30 transition-colors">
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{inf.name}</span>
                                                {inf.instagram_url && (
                                                    <a
                                                        href={inf.instagram_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-pink-500 hover:text-pink-600"
                                                    >
                                                        <Instagram className="w-4 h-4" />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <span className="font-semibold text-primary">
                                                {inf.followers_display || formatFollowers(inf.followers)}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex flex-wrap gap-1">
                                                {inf.categories?.slice(0, 3).map(cat => (
                                                    <Badge key={cat} variant="secondary" className="text-xs">
                                                        {cat}
                                                    </Badge>
                                                ))}
                                                {inf.categories?.length > 3 && (
                                                    <Badge variant="outline" className="text-xs">
                                                        +{inf.categories.length - 3}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3 text-muted-foreground">
                                            {inf.location || '-'}
                                        </td>
                                        <td className="p-3 max-w-[200px]">
                                            <span className="text-xs text-muted-foreground line-clamp-2">
                                                {inf.collaboration_details || '-'}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {inf.instagram_url && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => copyInstagram(inf.instagram_url)}
                                                        title="Link kopieren"
                                                    >
                                                        <Copy className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {isAdmin && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => { setEditingInfluencer(inf); setShowEditModal(true); }}
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-destructive"
                                                            onClick={() => handleDelete(inf.id)}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* Add Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Influencer hinzufügen</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div>
                            <Label>Name / Handle *</Label>
                            <Input
                                value={newInfluencer.name}
                                onChange={(e) => setNewInfluencer({ ...newInfluencer, name: e.target.value })}
                                placeholder="z.B. foodvienna"
                            />
                        </div>
                        
                        <div>
                            <Label>Instagram URL</Label>
                            <Input
                                value={newInfluencer.instagram_url}
                                onChange={(e) => setNewInfluencer({ ...newInfluencer, instagram_url: e.target.value })}
                                placeholder="https://instagram.com/..."
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Follower</Label>
                                <Input
                                    type="number"
                                    value={newInfluencer.followers}
                                    onChange={(e) => setNewInfluencer({ ...newInfluencer, followers: e.target.value })}
                                    placeholder="z.B. 50000"
                                />
                            </div>
                            <div>
                                <Label>Anzeige</Label>
                                <Input
                                    value={newInfluencer.followers_display}
                                    onChange={(e) => setNewInfluencer({ ...newInfluencer, followers_display: e.target.value })}
                                    placeholder="z.B. 50k"
                                />
                            </div>
                        </div>
                        
                        <div>
                            <Label>Kategorien</Label>
                            <div className="flex gap-2 mb-2">
                                <Input
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    placeholder="Kategorie hinzufügen..."
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                                />
                                <Button type="button" variant="outline" onClick={addCategory}>
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {newInfluencer.categories.map(cat => (
                                    <Badge key={cat} variant="secondary" className="gap-1">
                                        {cat}
                                        <X 
                                            className="w-3 h-3 cursor-pointer" 
                                            onClick={() => removeCategory(cat)}
                                        />
                                    </Badge>
                                ))}
                            </div>
                        </div>
                        
                        <div>
                            <Label>Standort</Label>
                            <AddressInput
                                value={newInfluencer.location}
                                onChange={(v) => setNewInfluencer({ ...newInfluencer, location: v })}
                                placeholder="z.B. Wien"
                            />
                        </div>
                        
                        <div>
                            <Label>Preise / Kollaboration</Label>
                            <Textarea
                                value={newInfluencer.collaboration_details}
                                onChange={(e) => setNewInfluencer({ ...newInfluencer, collaboration_details: e.target.value })}
                                placeholder="z.B. Reel + Stories = 150€"
                                rows={2}
                            />
                        </div>
                        
                        <div>
                            <Label>Notizen</Label>
                            <Textarea
                                value={newInfluencer.notes}
                                onChange={(e) => setNewInfluencer({ ...newInfluencer, notes: e.target.value })}
                                placeholder="Interne Notizen..."
                                rows={2}
                            />
                        </div>
                        
                        <div className="flex gap-2 pt-4">
                            <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                                Abbrechen
                            </Button>
                            <Button className="flex-1" onClick={handleCreate}>
                                Erstellen
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Influencer bearbeiten</DialogTitle>
                    </DialogHeader>
                    
                    {editingInfluencer && (
                        <div className="space-y-4">
                            <div>
                                <Label>Name / Handle *</Label>
                                <Input
                                    value={editingInfluencer.name}
                                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, name: e.target.value })}
                                />
                            </div>
                            
                            <div>
                                <Label>Instagram URL</Label>
                                <Input
                                    value={editingInfluencer.instagram_url || ''}
                                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, instagram_url: e.target.value })}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Follower</Label>
                                    <Input
                                        type="number"
                                        value={editingInfluencer.followers || ''}
                                        onChange={(e) => setEditingInfluencer({ ...editingInfluencer, followers: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Anzeige</Label>
                                    <Input
                                        value={editingInfluencer.followers_display || ''}
                                        onChange={(e) => setEditingInfluencer({ ...editingInfluencer, followers_display: e.target.value })}
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <Label>Kategorien (kommagetrennt)</Label>
                                <Input
                                    value={editingInfluencer.categories?.join(', ') || ''}
                                    onChange={(e) => setEditingInfluencer({ 
                                        ...editingInfluencer, 
                                        categories: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                                    })}
                                    placeholder="Lifestyle, Food, Mode"
                                />
                            </div>
                            
                            <div>
                                <Label>Standort</Label>
                                <AddressInput
                                    value={editingInfluencer.location || ''}
                                    onChange={(v) => setEditingInfluencer({ ...editingInfluencer, location: v })}
                                    placeholder="z.B. Wien"
                                />
                            </div>
                            
                            <div>
                                <Label>Preise / Kollaboration</Label>
                                <Textarea
                                    value={editingInfluencer.collaboration_details || ''}
                                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, collaboration_details: e.target.value })}
                                    rows={2}
                                />
                            </div>
                            
                            <div>
                                <Label>Notizen</Label>
                                <Textarea
                                    value={editingInfluencer.notes || ''}
                                    onChange={(e) => setEditingInfluencer({ ...editingInfluencer, notes: e.target.value })}
                                    rows={2}
                                />
                            </div>
                            
                            <div className="flex gap-2 pt-4">
                                <Button variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>
                                    Abbrechen
                                </Button>
                                <Button className="flex-1" onClick={handleUpdate}>
                                    Speichern
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default InfluencersPage;
